import React, { useState, useEffect } from "react";
import {
  Modal,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { useAuth } from "../../../context/AuthProvider";
import { HttpService } from "../../../service/HttpService";
import { toast } from "react-toastify";

const QtyModal = ({
  open,
  handleClose,
  mode = "purchased",
  itemDetails,
  fetchInventory,
  history = {},
}) => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const isUsed = mode === "used";

  const loggeduser = JSON.parse(localStorage.getItem("user"));
  const loggedInId = loggeduser?.id;

  // State for units
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const isEditMode = history && Object.keys(history).length > 0;

  // Fetch units
  const fetchUnits = () => {
    setLoading(true);
    httpService
      .get("inventory/units")
      .then((response) => {
        setUnits(response.data || []);
        setTimeout(() => setLoading(false), 300);
      })
      .catch((error) => {
        console.error("Error fetching units:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      fetchUnits();
    }
  }, [open]);

  // Initialize formik
  const formik = useFormik({
    enableReinitialize: true, // 👈 allow reinit on prop changes
    initialValues: {
      date: history?.date ? dayjs(history.date).format("YYYY-MM-DD") : null,
      qty: parseFloat(history?.purchased_qty) || "",
      unit_id: history?.unit_id
        ? history?.unit_id
        : itemDetails?.unit_id
        ? itemDetails?.unit_id
        : "",
      inventory_id: itemDetails?.id || "",
      user_id: loggedInId,
      type: mode,
    },
    validationSchema: Yup.object({
      date: Yup.date().required(
        `${isUsed ? "Used" : "Purchased"} date is required`
      ),
      qty: Yup.number()
        .typeError(`${isUsed ? "Used" : "Purchased"} quantity must be a number`)
        .required(`${isUsed ? "Used" : "Purchased"} quantity is required`),
      unit_id: Yup.string().required("Unit is required"),
    }),
    onSubmit: async (values) => {
      try {
        const fullDateTime = `${values.date} ${dayjs().format("HH:mm:ss")}`;
        const payload = {
          date: fullDateTime,
          qty: values.qty,
          inventory_id: values.inventory_id,
          user_id: values.user_id,
          type: values.type,
          unit_id: values.unit_id,
        };

        let response;
        if (isEditMode) {
          // 👇 Update existing record
          response = await httpService.put(
            `/inventory/transactions/${history.id}`,
            payload
          );
        } else {
          // 👇 Create new record
          response = await httpService.post("/inventory/transactions", payload);
        }

        if (response && response.success) {
          formik.resetForm();
          handleClose();
          fetchInventory();
          toast.success(
            `Quantity ${isEditMode ? "updated" : "added"} successfully`,
            { position: "top-right" }
          );
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error("Something went wrong!", { position: "top-right" });
      }
    },
  });

  return (
    <Modal open={open} onClose={handleClose}>
      <form onSubmit={formik.handleSubmit}>
        <div className="modal_block" style={modalStyle}>
          <div className="modal_header p-0">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">
                {isEditMode
                  ? `Edit ${isUsed ? "Used" : "Purchased"} Qty`
                  : isUsed
                  ? "Used Qty"
                  : "Purchased Qty"}
              </h2>
            </div>
          </div>

          <div className="modal_content p-0">
            <div className="row m-0">
              {/* Item Name */}
              <div className="col-md-12 p-0 pb-3">
                <label style={labelStyle}>Item Name</label>
                <div style={ItemName}>{itemDetails?.item_name || ""}</div>
              </div>

              {/* Date Picker */}
              <div className="col-md-12 p-0">
                <label style={labelStyle}>
                  {isUsed ? "Used Date" : "Purchased Date"}{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      formik.values.date ? dayjs(formik.values.date) : null
                    }
                    onChange={(newDate) =>
                      formik.setFieldValue(
                        "date",
                        dayjs(newDate).format("YYYY-MM-DD")
                      )
                    }
                    maxDate={dayjs()}
                    renderInput={(params) => (
                      <TextField fullWidth {...params} />
                    )}
                  />
                </LocalizationProvider>
                {formik.touched.date && formik.errors.date && (
                  <div style={{ color: "red" }}>{formik.errors.date}</div>
                )}
              </div>

              {/* Quantity & Unit */}
              <div className="col-md-12 p-0 mt-3">
                <div className="row g-3">
                  {/* Quantity */}
                  <div className="col-sm-8">
                    <label style={labelStyle}>
                      {isUsed ? "Used Qty" : "Purchased Qty"}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <TextField
                      fullWidth
                      name="qty"
                      value={formik.values.qty}
                      onChange={formik.handleChange}
                      error={formik.touched.qty && Boolean(formik.errors.qty)}
                      helperText={formik.touched.qty && formik.errors.qty}
                      placeholder="Enter Qty"
                    />
                  </div>

                  {/* Unit Dropdown */}
                  <div className="col-sm-4">
                    <label style={labelStyle}>
                      Unit <span style={{ color: "red" }}>*</span>
                    </label>
                    <FormControl fullWidth>
                      <Select
                        name="unit_id"
                        value={formik.values.unit_id}
                        onChange={formik.handleChange}
                        displayEmpty
                        disabled={loading}
                        error={
                          formik.touched.unit_id &&
                          Boolean(formik.errors.unit_id)
                        }
                      >
                        <MenuItem value="">
                          {loading ? "Loading..." : "Select Unit"}
                        </MenuItem>
                        {units.map((unit) => (
                          <MenuItem key={unit.id} value={unit.id}>
                            {unit.unit_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {formik.touched.unit_id && formik.errors.unit_id && (
                      <div style={{ color: "red" }}>
                        {formik.errors.unit_id}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="modal_footer justify-content-center mt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-border"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default QtyModal;

// ---------- Styles ----------
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#f6f9fc",
  borderRadius: 8,
  padding: "24px",
  width: 400,
  boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
};

const labelStyle = {
  fontWeight: 500,
  marginBottom: 4,
  display: "block",
};

const ItemName = {
  color: "#333533",
  fontSize: "20px",
  fontWeight: 400,
  lineHeight: "30px",
};
